/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

// tslint:disable:variable-name

import { SortDirection } from '@gh/core-data';
import { isNotNil, setArrayElement } from '@gh/core-util';
import { find, findIndex, max } from 'lodash';

import { GridSortModel } from '../state';

export type ColumnEnableConditions = {
  [key: string]: () => boolean;
};

export type GridColumn = {
  column: string;
  position: number;
  width: number;
  visible: boolean;
  resizable: boolean;
  sortable: boolean;
};

export type GridLayout = {
  name: string;
  columns: GridColumn[];
  pageSize: number;
  sort?: GridSortModel;
};

export const onlyVisible = ({ visible = true }: GridColumn) => visible;
export const onlyInvisible = ({ visible = true }: GridColumn) => !visible;
export const byColumn = ({ column }: GridColumn) => column;

export const updateGridColumn = (layout: GridLayout, name: string, transformer: IdentityFn<GridColumn>): GridLayout => {
  const columnIndex = findIndex(layout.columns, { column: name });
  if (columnIndex < 0) {
    return layout;
  }

  return {
    ...layout,
    columns: setArrayElement(layout.columns, columnIndex, transformer(layout.columns[columnIndex])),
  };
};

export const getGridColumn = (layout: GridLayout, name: string): Maybe<GridColumn> =>
  find(layout.columns, { column: name });

export const setGridColumnWidth = (layout: GridLayout, name: string, width: number): GridLayout =>
  updateGridColumn(layout, name, (column) => ({ ...column, width }));

export const setGridColumnVisibility = (layout: GridLayout, name: string, visible: boolean): GridLayout =>
  updateGridColumn(layout, name, (column) => ({
    ...column,
    visible,
    position: (max(layout.columns.filter(onlyVisible).map(({ position }) => position)) || 0) + 1,
  }));

export const isGridColumnVisible = (layout: GridLayout, name: string) => {
  const column = getGridColumn(layout, name);
  return column ? column.visible : false;
};

export const fromGridDirection = (sort: string) => ({
  'asc': SortDirection.Ascending,
  'desc': SortDirection.Descending,
}[sort]);

export const toGridDirection = (direction: SortDirection) => ({
  [SortDirection.Ascending]: 'asc',
  [SortDirection.Descending]: 'desc',
}[direction]);

export class ManagedGridElement {
  protected unexpectedChange(): void {
    throw new Error('ManagedGridLayout: unexpected change of layout: use update method to update layout');
  }
}

export class ManagedGridColumn extends ManagedGridElement implements GridColumn {
  static create(column: GridColumn): ManagedGridColumn {
    return column instanceof ManagedGridColumn ? <ManagedGridColumn>column : new ManagedGridColumn(column);
  }

  private _column: string;
  private _position: number;
  private _width: number;
  private _visible: boolean;
  private _resizable: boolean;
  private _sortable: boolean;

  set column(_: string) {
    this.unexpectedChange();
  }

  get column(): string {
    return this._column;
  }

  set position(_: number) {
    this.unexpectedChange();
  }

  get position(): number {
    return this._position;
  }

  set width(_: number) {
    this.unexpectedChange();
  }

  get width(): number {
    return this._width;
  }

  set visible(_: boolean) {
    this.unexpectedChange();
  }

  get visible(): boolean {
    return this._visible;
  }

  set resizable(_: boolean) {
    this.unexpectedChange();
  }

  get resizable(): boolean {
    return this._resizable;
  }

  set sortable(_: boolean) {
    this.unexpectedChange();
  }

  get sortable(): boolean {
    return this._sortable;
  }

  constructor(column: GridColumn) {
    super();

    this.update(column);
  }

  update(column: GridColumn): void {
    this.merge(column);
  }

  merge(column: Partial<GridColumn>): void {
    this._column = isNotNil(column.column) ? column.column : this._column;
    this._position = isNotNil(column.position) ? column.position : this._position;
    this._width = isNotNil(column.width) ? column.width : this._width;
    this._visible = isNotNil(column.visible) ? column.visible : this._visible;
    this._resizable = isNotNil(column.resizable) ? column.resizable : this._resizable;
    this._sortable = isNotNil(column.sortable) ? column.sortable : this._sortable;
  }

  toObject(): GridColumn {
    return {
      column: this._column,
      position: this._position,
      width: this._width,
      visible: this._visible,
      resizable: this._resizable,
      sortable: this._sortable,
    };
  }
}

/**
 * Managed GridLayout is necessary for explicit (!!!) sharing of grid layout
 * and explicit (!!!) description of mutations.
 *
 * The idea is to share instance of this class between top level and grid components.
 * Grid component can mutate layout and share this changes with top level component.
 */
export class ManagedGridLayout extends ManagedGridElement implements GridLayout {
  static create(layout: GridLayout): ManagedGridLayout {
    return layout instanceof ManagedGridLayout ? <ManagedGridLayout>layout : new ManagedGridLayout(layout);
  }

  private _name: string;
  private _columns: ManagedGridColumn[];
  private _pageSize: number;
  private _sort?: GridSortModel;

  set name(_: string) {
    this.unexpectedChange();
  }

  get name(): string {
    return this._name;
  }

  set columns(_: ManagedGridColumn[]) {
    this.unexpectedChange();
  }

  get columns(): ManagedGridColumn[] {
    return this._columns;
  }

  set pageSize(_: number) {
    this.unexpectedChange();
  }

  get pageSize(): number {
    return this._pageSize;
  }

  set sort(_: Maybe<GridSortModel>) {
    this.unexpectedChange();
  }

  get sort(): Maybe<GridSortModel> {
    return this._sort;
  }

  constructor(layout: GridLayout) {
    super();

    this.update(layout);
  }

  update(layout: GridLayout): void {
    this.merge(layout);
  }

  merge(layout: Partial<GridLayout>): void {
    this._name = isNotNil(layout.name) ? layout.name : this._name;
    this._columns = isNotNil(layout.columns) ? layout.columns.map(ManagedGridColumn.create) : this._columns;
    this._pageSize = isNotNil(layout.pageSize) ? layout.pageSize : this._pageSize;
    this._sort = isNotNil(layout.sort) ? layout.sort : this._sort;
  }

  toObject(): GridLayout {
    return {
      name: this._name,
      columns: this._columns.map((column) => column.toObject()),
      pageSize: this._pageSize,
      sort: this._sort,
    };
  }
}
